import { RefreshCW02 } from "untitledui-js-base";
import { FC } from "react";
import { Button, ButtonSize, ButtonVariant, Modal } from "shared/components";
import { Money } from "types/landlord/reports";
import { formatToCurrency } from "shared/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { verificationNameToTitle } from "../../applications-id/components/ApplicantsTable/utils";
import { VerificationNames } from "shared/types";
import { logger } from "logger";

export const RerunModal: FC<{
  open: boolean;
  onClose: () => void;
  reportFee: Money;
  applicantId: number;
  applicationId: number;
  verificationId: number;
  verificationName: VerificationNames;
}> = ({
  open,
  onClose,
  reportFee,
  applicantId,
  applicationId,
  verificationId,
  verificationName
}) => {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      instance.post(
        `/portal/v1/applications/${applicationId}/applicants/${applicantId}/verifications/${verificationId}/rerun`
      ),
    onSuccess: () => {
      toast.success("Report successfully re-run");
      queryClient
        .refetchQueries({
          queryKey: ["APPLICATION", String(applicationId)]
        })
        .catch((error) => {
          logger.error("Failed to invalidate queries", error);
        });
      onClose();
    },
    onError: (error: Error) => {
      toast.error(`Error re-running report: ${getErrorMessage(error)}`);
    }
  });

  return (
    <Modal
      opened={open}
      onClose={() => onClose()}
      panelClassName="p-6 max-w-[400px] w-full space-y-4"
    >
      <div className="rounded-full bg-alpha-40 p-4 mx-auto size-16">
        <RefreshCW02 size="32px" className="stroke-content-400" />
      </div>
      <div className="space-y-2">
        <h1 className="text-xl font-bold text-center">Re-run report?</h1>
        <p className="text-center text-content-500 text-base">
          {`Re-running the ${verificationNameToTitle(verificationName).toLowerCase()} will be billed at your contracted cost of
           ${formatToCurrency(reportFee)} for the ${verificationNameToTitle(verificationName).toLowerCase()}`}
        </p>
      </div>
      <div className="flex gap-2 w-full">
        <Button
          size={ButtonSize.BASE}
          variant={ButtonVariant.BASE}
          className="w-full"
          onClick={() => onClose()}
        >
          Cancel
        </Button>
        <Button
          size={ButtonSize.BASE}
          variant={ButtonVariant.PRIMARY}
          className="w-full"
          loading={isPending}
          disabled={isPending}
          onClick={() => mutate()}
        >
          Re-run
        </Button>
      </div>
    </Modal>
  );
};
